import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export interface AuditEvent {
  eventId: string
  seq: number
  eventType: string
  aggregateType: string
  aggregateId: string
  actorType: string
  actorUserId: string | null
  actorEmpId: string | null
  occurredAt: string
  recordedAt: string
  source: string
  sourceBatchId: string | null
  payloadJson: string
}

export interface AuditPrefix {
  prefix: string
  count: number
}

export interface AuditQuery {
  from?: string
  to?: string
  actor?: string
  type?: string
  source?: string
  aggregateId?: string
  limit?: number
  offset?: number
}

function filterParams(query: AuditQuery): Record<string, string> {
  const params: Record<string, string> = {}
  if (query.from) params.from = query.from
  if (query.to) params.to = query.to
  if (query.actor) params.actor = query.actor
  if (query.type) params.type = query.type
  if (query.source) params.source = query.source
  if (query.aggregateId) params.aggregateId = query.aggregateId
  return params
}

export function useAuditEvents(query: AuditQuery) {
  return useQuery({
    queryKey: ['audit', 'events', query],
    queryFn: async () => {
      const params = { ...filterParams(query), limit: query.limit ?? 200, offset: query.offset ?? 0 }
      const res = await api.get<AuditEvent[]>('/v1/audit', { params })
      return res.data
    },
    // Keep the current page on screen while the next one loads.
    placeholderData: keepPreviousData,
  })
}

/** How many events the filters match, for "page 2 of 14"; limit/offset ignored. */
export function useAuditCount(query: AuditQuery) {
  const { limit: _limit, offset: _offset, ...filters } = query
  return useQuery({
    queryKey: ['audit', 'count', filters],
    queryFn: async () =>
      (await api.get<{ total: number }>('/v1/audit/count', { params: filterParams(filters) })).data.total,
    placeholderData: keepPreviousData,
  })
}

export function useAuditPrefixes(from?: string, to?: string) {
  return useQuery({
    queryKey: ['audit', 'prefixes', from, to],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      const res = await api.get<AuditPrefix[]>('/v1/audit/prefixes', { params })
      return res.data
    },
  })
}
